import React, { useState } from "react";
import { connect, useDispatch } from 'react-redux'
import Link from "next/link";

import { QuestionIcon } from "../../modules/Icons";
import { Tooltip } from 'react-tooltip';
import LicenseWarningTag from '../products/LicenseWarningTag';;
import LicenseConfirmationMinimalFlowModal from '../license-confirmation-modals/LicenseConfirmationMinimalFlowModal';

import { ApplicationState } from '../../types/state/storeTypes';
import { MiniLicenseProfileStateProps, MiniLicenseProfileProps } from '../../types/components/license-profile/MiniLicenseProfile'
import { User } from "../../types/api/UsersTypes";
import { SubscriptionDetail } from "../../types/api/SubscriptionTypes";

import { ONBOARDING_MODAL_ACTIONS } from '../../constants/actions';
const { OPEN_ONBOARDING_MODAL } = ONBOARDING_MODAL_ACTIONS;

const MiniLicenseProfile: React.FC<MiniLicenseProfileProps> = ({ setLicenseSelectionAssistDialogOpen, user, subscription, collectionOwned }) => {
    const [licenseConfirmationFlowOpen, setLicenseConfirmationFlowOpen] = useState<boolean>(false);
    const dispatch = useDispatch()

    const determineLicensedToName = () => {
      const licenseeProfile = user?.team?.licensee_profile;
      if (!!licenseeProfile)
        return licenseeProfile?.company_name || licenseeProfile?.first_name + ' ' + licenseeProfile?.last_name;
      return user.first_name + ' ' + user.last_name
    }

    const licenseIsKnown = (): boolean => {
      return !!user?.min_license_id ||
             !!user?.team?.licensee_profile ||
             !!subscription?.license?.id;
    }

    const licenseIsInvalidated = (): boolean => {
      return !!user?.team?.licensee_profile?.invalidated;
    }

    const getLicenseName = (): string => {
      const minLicenseId = Math.max(
        user?.min_license_id || 1,
        user?.team?.licensee_profile?.license_id || 1,
        subscription?.license?.id || 1
      );
      switch (minLicenseId) {
        case 1:
          return 'Individual';
        case 2:
          return 'Startup';
        case 3:
        case 4:
        case 5:
        case 6:
          return 'Enterprise';
        default:
          return;
      }
    }

    const openOnboardingFlowModal = () => {
      user?.id ?
        setLicenseConfirmationFlowOpen(true) :
        dispatch({ type: OPEN_ONBOARDING_MODAL });
    }

    const unknownLicenseUi = () => {
      return (
        <>
          <div className="flex justify-between">
            <div className='flex items-center cursor-pointer'>
              <span className="text-[16px] font-bold">Unknown</span>
              <QuestionIcon data-tooltip-id="license" className="flex-shrink-0 inline ml-2" />
              <Tooltip
                id="license"
                className="max-w-[260px] z-40 !text-[14px] !normal-case leading-[22px] rounded-[5px] py-[10px]"
                opacity={1}
                clickable={true}
                style={{ backgroundColor: 'black' }}>
                The license associated with your account. Want to learn more about licenses? <span className='text-a-blue' onClick={() => setLicenseSelectionAssistDialogOpen(true)}>Read this</span>
              </Tooltip>
            </div>
            <span className="text-[15px] underline text-a-light-blue cursor-pointer" onClick={openOnboardingFlowModal}>Set license</span>
          </div>
          <span className="text-[14px] font-light leading-[22px] tracking-wide">You will need to confirm your license before you can purchase items.</span>
        </>
      )
    }

    const knownLicenseUi = () => {
      return (
        <div className="flex justify-between">
          <div className="flex flex-col">
            <div className='flex items-center cursor-pointer'>
              <span className='capitalize font-semibold text-[16px]'>{getLicenseName()}</span>
              {licenseIsInvalidated() && <LicenseWarningTag className="ml-2" />}
              <QuestionIcon data-tooltip-id="license" className="flex-shrink-0 inline ml-2" />
              <Tooltip
                id="license"
                className="max-w-[260px] z-40 !text-[14px] !normal-case leading-[22px] rounded-[5px] py-[10px]"
                opacity={1}
                clickable={true}
                style={{ backgroundColor: 'black' }}>
                The license associated with your account. Unsure if you have the right license? <span className='text-a-blue' onClick={() => setLicenseSelectionAssistDialogOpen(true)}>Read this</span>
              </Tooltip>
            </div>
            <span className='text-[14px]'>Licensed to: {determineLicensedToName()}</span>
          </div>
          {collectionOwned && user.min_license_id && !user?.team?.licensee_profile?.id && <Link href="/accounts/profile" className="text-[15px] underline text-a-light-blue cursor-pointer">Complete License</Link>}
        </div>
      )
    }

  return (
    <div className='flex flex-col gap-1 md:pt-8' cy-test-id="mini-license-profile">
      <span className='uppercase text-[#7F898D] text-[14px]'>Your License</span>
      {licenseIsKnown() ? knownLicenseUi() : unknownLicenseUi()}
      <LicenseConfirmationMinimalFlowModal
        isOpen={licenseConfirmationFlowOpen}
        setIsOpen={setLicenseConfirmationFlowOpen}
      />
    </div>
  );
};

const mapStateToProps = (state: ApplicationState): MiniLicenseProfileStateProps => ({
  user: state.auth.user as User,
  subscription: state.auth.subscription as SubscriptionDetail,
});

export default connect(mapStateToProps)(MiniLicenseProfile);
